import { createApi } from "@reduxjs/toolkit/query/react";
import { getAxiosInstance } from "../../../api";

const axiosBaseQuery =
  () =>
  async ({ params, endpoint, method, body }) => {
    const api = await getAxiosInstance();
    const getParams = (key) => {
      if (params?.[key]) {
        return `${key}=${params[key]}&`;
      } else {
        return "";
      }
    };
    if (method === "get") {
      try {
        const response = await api[method](
          `${endpoint}?${getParams("start")}${getParams("end")}filter=${
            params?.filter || "all"
          }&${getParams("sort_by")}sort_order=${
            params?.sort_order || "desc"
          }&per_page=${params?.page_size || 20}&${getParams("search")}page=${
            params?.page || 1
          }&${getParams("receipt_id")}&${getParams("process_slug")}&${getParams(
            "stock_receipt_id",
          )}&${getParams("cash_receipt_id")}`,
          body,
        );
        return { data: response.data };
      } catch (axiosError) {
        let err = axiosError;
        return {
          error: {
            status: err.response?.status,
            data: err.response?.data || err.message,
          },
        };
      }
    } else {
      try {
        const response = await api[method](
          `${endpoint}${getParams("start")}${getParams("end")}${getParams(
            "sort_by",
          )}${getParams("search")}`,
          body,
        );
        return { data: response.data };
      } catch (axiosError) {
        let err = axiosError;
        return {
          error: {
            status: err.response?.status,
            data: err.response?.data || err.message,
          },
        };
      }
    }
  };

export const delivery = createApi({
  reducerPath: "deliveryApi",
  baseQuery: axiosBaseQuery(),
  tagTypes: [
    "Delivery",
    "DeliveryRecheck",
    "Certificate Edit",
    "DeliveryComplete",
    "DeliveryChallan",
    "DeliveryChallanUploads",
    "CashReceiptPending",
    "CashReceiptCompleted",
    "SupplierPaymentPending",
    "DeliveryOldCardReissue",
  ],
  endpoints: (builder) => ({
    //orders  starts
    getCertificateListData: builder.query({
      query: (values) => ({
        method: "get",
        params: values,
        endpoint: `delivery/list`,
      }),
      providesTags: ["Delivery"],
    }),
    //recheck starts
    getRecheckCertificateListData: builder.query({
      query: (values) => ({
        method: "get",
        params: values,
        endpoint: `delivery/recheck/list`,
      }),
      providesTags: ["DeliveryRecheck"],
    }),
    //reissue starts
    getReissueCertificateListData: builder.query({
      query: (values) => ({
        method: "get",
        params: values,
        endpoint: `delivery/reissue/list`,
      }),
      providesTags: ["DeliveryReissue"],
    }),

    //old card reissue starts
    getOldCardReissueCertificateListData: builder.query({
      query: (values) => ({
        method: "get",
        params: values,
        endpoint: `delivery/old-certificate/list`,
      }),
      providesTags: ["DeliveryOldCardReissue"],
    }),

    // completed starts
    getCompletedDeliveryListData: builder.query({
      query: (body) => ({
        method: "post",
        body,
        endpoint: `delivery/delivery-complete-list`,
      }),
      providesTags: ["DeliveryComplete"],
    }),
    getCompletedDeliveryRecheckReissueListData: builder.query({
      query: (body) => ({
        method: "post",
        body,
        endpoint: `delivery/delivery-complete-recheck-reissue-list`,
      }),
      providesTags: ["DeliveryComplete"],
    }),

    updateDeliveryCompletedTableHeadData: builder.mutation({
      query: (body) => ({
        method: "post",
        body,
        endpoint: `/user-fields/save`,
      }),
      invalidatesTags: ["DeliveryComplete"],
    }),

    // delivery challan
    getVerifiedStocksListData: builder.query({
      query: (values) => ({
        method: "get",
        params: values,
        endpoint: `delivery/verified-stocks`,
      }),
      providesTags: ["DeliveryChallan"],
    }),

    getDeliveryChallanUploadsListData: builder.query({
      query: (values) => ({
        method: "get",
        params: values,
        endpoint: `delivery/delivery-challan-uploads-list`,
      }),
      providesTags: ["DeliveryChallanUploads"],
    }),

    // cash receipt (Delivery > Invoices)
    getAvailableInvoicesListData: builder.query({
      query: (body) => ({
        method: "post",
        body,
        endpoint: `delivery/cash-receipt/available-invoices`,
      }),
      providesTags: ["CashReceiptPending"],
    }),
    getCashReceiptListData: builder.query({
      query: (body) => ({
        method: "post",
        body,
        endpoint: `delivery/cash-receipt/list`,
      }),
      providesTags: ["CashReceiptCompleted"],
    }),
    getCashReceiptSummary: builder.mutation({
      query: (body) => ({
        method: "post",
        body,
        endpoint: `delivery/cash-receipt/summary`,
      }),
    }),
    storeCashReceipt: builder.mutation({
      query: (body) => ({
        method: "post",
        body,
        endpoint: `delivery/cash-receipt/store`,
      }),
      invalidatesTags: ["CashReceiptPending", "CashReceiptCompleted"],
    }),
    // cash receipt > supplier payment (Pending > Supplier sub-tab)
    getSupplierPaymentListData: builder.query({
      query: (body) => ({
        method: "post",
        body,
        endpoint: `delivery/cash-receipt/suppliers`,
      }),
      providesTags: ["SupplierPaymentPending"],
    }),
    storeSupplierPayment: builder.mutation({
      query: (body) => ({
        method: "post",
        body,
        endpoint: `delivery/cash-receipt/supplier-payment/store`,
      }),
      invalidatesTags: ["SupplierPaymentPending", "CashReceiptCompleted"],
    }),
    reprintCashReceipt: builder.mutation({
      query: (values) => ({
        method: "get",
        params: values,
        endpoint: `delivery/cash-receipt/reprint`,
      }),
    }),
    editCashReceipt: builder.mutation({
      query: (values) => ({
        method: "get",
        params: values,
        endpoint: `delivery/cash-receipt/edit`,
      }),
    }),
    updateCashReceipt: builder.mutation({
      query: (body) => ({
        method: "post",
        body,
        endpoint: `delivery/cash-receipt/update`,
      }),
      invalidatesTags: [
        "CashReceiptCompleted",
        "CashReceiptPending",
        "SupplierPaymentPending",
      ],
    }),
    cancelCashReceipt: builder.mutation({
      query: (body) => ({
        method: "post",
        body,
        endpoint: `delivery/cash-receipt/cancel`,
      }),
      invalidatesTags: [
        "CashReceiptCompleted",
        "CashReceiptPending",
        "SupplierPaymentPending",
      ],
    }),
    updateCashReceiptTableHeadData: builder.mutation({
      query: (body) => ({
        method: "post",
        body,
        endpoint: `/user-fields/save`,
      }),
      invalidatesTags: ["CashReceiptCompleted", "CashReceiptPending"],
    }),
  }),
});

export const {
  useGetCertificateListDataQuery,
  useGetRecheckCertificateListDataQuery,
  useGetReissueCertificateListDataQuery,
  useGetOldCardReissueCertificateListDataQuery,
  useGetCompletedDeliveryListDataQuery,
  useGetCompletedDeliveryRecheckReissueListDataQuery,
  useUpdateDeliveryCompletedTableHeadDataMutation,
  useGetVerifiedStocksListDataQuery,
  useGetDeliveryChallanUploadsListDataQuery,
  useGetAvailableInvoicesListDataQuery,
  useGetCashReceiptListDataQuery,
  useGetCashReceiptSummaryMutation,
  useStoreCashReceiptMutation,
  useGetSupplierPaymentListDataQuery,
  useStoreSupplierPaymentMutation,
  useReprintCashReceiptMutation,
  useEditCashReceiptMutation,
  useUpdateCashReceiptMutation,
  useCancelCashReceiptMutation,
  useUpdateCashReceiptTableHeadDataMutation,
} = delivery;
